import gsap from 'gsap';

// This is a refactored version(that fixes all the ts issues) of the official seamlessLoop() utility in GSAP creates a continuous, looping animation for elements, ensuring smooth transitions without visible jumps or gaps. This utility is ideal for creating visually appealing effects where items scroll seamlessly in a loop
// https://gsap.com/docs/v3/HelperFunctions/helpers/seamlessLoop

const seamlessLoop = (
  items: HTMLElement[] | string,
  config?: InfiniteScrollConfig
): ScrollTimeline => {
  const targets = gsap.utils.toArray<HTMLElement>(items);
  const cfg = config || {};
  const tl = gsap.timeline({
    repeat: cfg.repeat,
    paused: cfg.paused,
    defaults: { ease: 'none' },
    onReverseComplete: () => {
      tl.totalTime(tl.rawTime() + tl.duration() * 100);
    },
  }) as ScrollTimeline;

  const length = targets.length;
  if (length === 0 || !targets[0]) {
    return tl;
  }
  const startX = targets[0].offsetLeft;
  const times: number[] = [];
  const widths: number[] = [];
  const xPercents: number[] = [];
  let curIndex = 0;

  const pixelsPerSecond = (cfg.speed || 1) * 100;
  let snap: (_value: number) => number;
  if (typeof cfg.snap === 'function') {
    snap = cfg.snap;
  } else if (cfg.snap === false) {
    snap = (v: number) => v;
  } else {
    snap = gsap.utils.snap(cfg.snap ?? 1);
  }

  gsap.set(targets, {
    xPercent: (i, el) => {
      const w = (widths[i] = parseFloat(gsap.getProperty(el, 'width', 'px') as string));
      xPercents[i] = snap(
        (parseFloat(gsap.getProperty(el, 'x', 'px') as string) / w) * 100 +
          (gsap.getProperty(el, 'xPercent') as number)
      );
      return xPercents[i] ?? 0;
    },
  });

  gsap.set(targets, { x: 0 });

  const last = targets[length - 1];
  if (!last) {
    return tl;
  }
  const totalWidth =
    last.offsetLeft +
    ((xPercents[length - 1] ?? 0) / 100) * (widths[length - 1] ?? 0) -
    startX +
    last.offsetWidth * (gsap.getProperty(last, 'scaleX') as number) +
    (parseFloat(cfg.paddingRight?.toString() || '0') || 0);

  for (let i = 0; i < length; i++) {
    const item = targets[i];
    if (!item) continue;
    const curX = ((xPercents[i] ?? 0) / 100) * (widths[i] ?? 0);
    const distanceToStart = item.offsetLeft + curX - startX;
    const distanceToLoop =
      distanceToStart + (widths[i] ?? 0) * (gsap.getProperty(item, 'scaleX') as number);

    tl.to(
      item,
      {
        xPercent: snap(((curX - distanceToLoop) / (widths[i] ?? 1)) * 100),
        duration: distanceToLoop / pixelsPerSecond,
      },
      0
    )
      .fromTo(
        item,
        {
          xPercent: snap(((curX - distanceToLoop + totalWidth) / (widths[i] ?? 1)) * 100),
        },
        {
          xPercent: xPercents[i] ?? 0,
          duration: (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond,
          immediateRender: false,
        },
        distanceToLoop / pixelsPerSecond
      )
      .add(`label${i}`, distanceToStart / pixelsPerSecond);

    times[i] = distanceToStart / pixelsPerSecond;
  }

  function toIndex(index: number, vars: gsap.TweenVars = {}) {
    if (Math.abs(index - curIndex) > length / 2) {
      index += index > curIndex ? -length : length;
    }

    const newIndex = gsap.utils.wrap(0, length, index);
    let time = times[newIndex] ?? 0;

    if (time > tl.time() !== index > curIndex) {
      vars.modifiers = { time: gsap.utils.wrap(0, tl.duration()) };
      time += tl.duration() * (index > curIndex ? 1 : -1);
    }

    curIndex = newIndex;
    vars.overwrite = true;
    return tl.tweenTo(time, vars);
  }

  tl.next = (vars) => toIndex(curIndex + 1, vars);
  tl.previous = (vars) => toIndex(curIndex - 1, vars);
  tl.current = () => curIndex;
  tl.toIndex = (index, vars) => toIndex(index, vars);
  tl.times = times;
  tl.progress(1, true).progress(0, true);

  if (cfg.reversed) {
    tl.vars.onReverseComplete?.();
    tl.reverse();
  }

  return tl;
};

export default seamlessLoop;
